import $ from 'jquery';
import { getState } from '~src/state';
import { createGuideline } from './guidelineManagement';
import {
  GUIDELINE_COLOR,
  GUIDELINE_WIDTH,
  GUIDELINE_SNAP_COLOR,
  SNAP_HIGHLIGHT_WIDTH,
} from './constants';
import { findNearestRulerMark, findGuidelineSnapPoint } from './snapFunctions';

export const setupRulerEventListeners = (rulerEnabled, rulerUnit) => {
  // Remove any existing jQuery event handlers first to prevent duplicates
  $(document).off('mousedown.ruler');
  $(document).off('mouseenter.ruler');
  $(document).off('mouseleave.ruler');

  // Use document-level event delegation for rulers with namespaced events
  $(document).on('mousedown.ruler', '#horizontal-ruler', (e) => {
    if (!rulerEnabled()) return;
    e.preventDefault();
    e.stopPropagation();

    // Get the canvas element position to calculate relative position
    const canvasEl = window.canvas.getElement();
    const canvasRect = canvasEl.getBoundingClientRect();
    const zoom = getState('currentCanvasZoom', 1);

    // Create guideline at the click position on canvas
    // Since horizontal ruler is above canvas, use a position relative to canvas top
    const initialY = (e.clientY - canvasRect.top) / zoom;

    createGuideline('horizontal', initialY, rulerUnit());
  });

  $(document).on('mousedown.ruler', '#vertical-ruler', (e) => {
    if (!rulerEnabled()) return;
    e.preventDefault();
    e.stopPropagation();

    // Get the canvas element position to calculate relative position
    const canvasEl = window.canvas.getElement();
    const canvasRect = canvasEl.getBoundingClientRect();
    const zoom = getState('currentCanvasZoom', 1);

    // Create guideline at the click position on canvas
    // Since vertical ruler is to the left of canvas, use a position relative to canvas left
    const initialX = (e.clientX - canvasRect.left) / zoom;

    createGuideline('vertical', initialX, rulerUnit());
  });

  // Add hover effect with namespaced events
  $(document).on(
    'mouseenter.ruler',
    '#horizontal-ruler, #vertical-ruler',
    function () {
      if (!rulerEnabled()) return;
      $(this).css('opacity', '0.8');
    }
  );

  $(document).on(
    'mouseleave.ruler',
    '#horizontal-ruler, #vertical-ruler',
    function () {
      if (!rulerEnabled()) return;
      $(this).css('opacity', '1');
    }
  );
};

// Export handler for object:moving event
export const handleObjectMoving = (e, rulerUnit) => {
  if (e.target && e.target.isGuideline) {
    const guideline = e.target;
    const orientation = guideline.orientation;
    let targetPosition;

    if (orientation === 'horizontal') {
      // For horizontal lines, use top position
      targetPosition = guideline.top;
    } else {
      // For vertical lines, use left position
      targetPosition = guideline.left;
    }

    // First check for ruler mark snapping
    const rulerSnapThreshold = 5;
    const nearestRulerMark = findNearestRulerMark(
      targetPosition,
      rulerSnapThreshold,
      rulerUnit
    );

    // Then check for object/guideline snap points
    const snapResult = findGuidelineSnapPoint(
      targetPosition,
      orientation,
      guideline
    );

    // Decide which snap to use
    let finalSnapPosition = null;
    let snapType = null;

    if (nearestRulerMark !== null && snapResult.position !== null) {
      const rulerDistance = Math.abs(targetPosition - nearestRulerMark);
      const objectDistance = Math.abs(targetPosition - snapResult.position);

      if (rulerDistance <= objectDistance) {
        finalSnapPosition = nearestRulerMark;
        snapType = 'ruler';
      } else {
        finalSnapPosition = snapResult.position;
        snapType = 'object';
      }
    } else if (nearestRulerMark !== null) {
      finalSnapPosition = nearestRulerMark;
      snapType = 'ruler';
    } else if (snapResult.position !== null) {
      finalSnapPosition = snapResult.position;
      snapType = 'object';
    }

    if (finalSnapPosition !== null) {
      // Snap to the position
      targetPosition = finalSnapPosition;

      // Change appearance when snapped
      guideline.set({
        stroke: snapType === 'ruler' ? '#00ff00' : GUIDELINE_SNAP_COLOR,
        strokeWidth: SNAP_HIGHLIGHT_WIDTH,
        padding: guideline.customPadding || guideline.padding, // Preserve padding
      });
    } else {
      // Reset appearance when not snapped
      guideline.set({
        stroke: GUIDELINE_COLOR,
        strokeWidth: GUIDELINE_WIDTH,
        padding: guideline.customPadding || guideline.padding, // Preserve padding
      });
    }

    // Always update the guideline position
    const canvas = window.canvas;
    if (orientation === 'horizontal') {
      guideline.set({
        x1: 0,
        x2: canvas.getWidth(),
        y1: targetPosition,
        y2: targetPosition,
        top: targetPosition, // Also update top position
        left: 0, // Ensure left is at canvas start
        width: canvas.getWidth(), // Update width to match canvas
        padding: guideline.customPadding || guideline.padding, // Preserve padding
      });
    } else {
      guideline.set({
        x1: targetPosition,
        x2: targetPosition,
        y1: 0,
        y2: canvas.getHeight(),
        left: targetPosition, // Also update left position
        top: 0, // Ensure top is at canvas start
        height: canvas.getHeight(), // Update height to match canvas
        padding: guideline.customPadding || guideline.padding, // Preserve padding
      });
    }
    guideline.setCoords();
  }
};

// Export handler for object:modified event
export const handleObjectModified = (e) => {
  if (e.target && e.target.isGuideline) {
    e.target.set({
      stroke: GUIDELINE_COLOR,
      strokeWidth: GUIDELINE_WIDTH,
      padding: e.target.customPadding || e.target.padding, // Preserve padding
    });
    window.canvas.renderAll();
  }
};
